import { id } from "@suite/core";

export interface PageRef {
  id: string;
  /** Which loaded document this page came from. */
  docId: string;
  /** 1-based page number within its source document. */
  sourcePage: number;
  /** Extra rotation the user applied, in degrees clockwise. */
  rotation: number;
  /** Rendered thumbnail, once the worker has produced it. */
  bitmap?: ImageBitmap;
  width?: number;
  height?: number;
  /** The page's own /Rotate, in degrees clockwise (0, 90, 180, 270). */
  intrinsicRotation?: number;
  /** CropBox in PDF user space: [x1, y1, x2, y2]. Set once the worker has read the page. */
  view?: [number, number, number, number];
  /** Signatures and text placed on this page, in PDF user space. */
  stamps: Stamp[];
}

/** A signature or text image placed on a page. Geometry is in PDF points. */
export interface Stamp {
  id: string;
  /** Which saved artwork this is an instance of; images are embedded once per source. */
  sourceId: string;
  png: Uint8Array;
  /** Natural aspect ratio (width / height) of the artwork. */
  aspect: number;
  /** Lower-left corner of the stamp as it appears on screen, mapped into PDF space. */
  x: number;
  y: number;
  /** Size along the stamp's own axes (as displayed), in PDF points. */
  width: number;
  height: number;
  /** Total display rotation the stamp was placed under; it is drawn upright relative to that. */
  rotation: number;
}

export type FieldKind = "text" | "checkbox" | "radio" | "choice" | "signature";

/** One widget of an AcroForm field, as reported by pdf.js. */
export interface FormField {
  /** pdf.js annotation id — unique per widget. */
  id: string;
  /** Fully-qualified field name, shared by all widgets of one field (e.g. every radio button). */
  name: string;
  kind: FieldKind;
  /** 1-based page the widget sits on. */
  page: number;
  /** Widget rectangle in PDF user space: [x1, y1, x2, y2]. */
  rect: [number, number, number, number];
  /** Value stored in the file when it was opened. */
  initial: FieldValue;
  /** Checkbox: the value written when ticked. Radio: this button's own value. */
  onValue?: string;
  options?: { value: string; label: string }[];
  multiline?: boolean;
  multiSelect?: boolean;
  maxLen?: number;
  readOnly?: boolean;
  fontSize?: number;
  align?: "left" | "center" | "right";
}

/** Text/choice: the string; checkbox: true/false; radio: the selected button's value. */
export type FieldValue = string | string[] | boolean | null;

export interface LoadedDoc {
  id: string;
  name: string;
  size: number;
  pageCount: number;
  /** Kept for export; the worker copy was transferred away. */
  bytes: Uint8Array;
  /** Every form widget in the file; empty when it has no fillable form. */
  fields: FormField[];
  /** Values the user entered, keyed by field name. Absent = unchanged from the file. */
  fills: Record<string, FieldValue>;
}

export const STORAGE_KEY = "pdf-tools:prefs";

/** Above this, thumbnail rendering gets slow and memory-hungry on phones. */
export const LARGE_FILE_BYTES = 100 * 1024 * 1024;

export function makePageRefs(doc: LoadedDoc): PageRef[] {
  return Array.from({ length: doc.pageCount }, (_, index) => ({
    id: id("pg"),
    docId: doc.id,
    sourcePage: index + 1,
    rotation: 0,
    stamps: [],
  }));
}

/** Parses "1-3, 7, 9-" into 1-based page numbers within `total`. */
export function parseRanges(input: string, total: number): number[] {
  const pages = new Set<number>();
  for (const part of input.split(",")) {
    const chunk = part.trim();
    if (!chunk) continue;
    const range = /^(\d+)?\s*-\s*(\d+)?$/.exec(chunk);
    // A bare "-" is a typo, not a request for every page: requiring at least
    // one bound stops a slip from exporting the whole document.
    if (range && (range[1] !== undefined || range[2] !== undefined)) {
      const start = range[1] ? Number(range[1]) : 1;
      const end = range[2] ? Number(range[2]) : total;
      for (let p = Math.max(1, start); p <= Math.min(total, end); p += 1) pages.add(p);
      continue;
    }
    const single = Number(chunk);
    if (Number.isInteger(single) && single >= 1 && single <= total) pages.add(single);
  }
  return [...pages].sort((a, b) => a - b);
}

export function downloadBytes(bytes: Uint8Array, filename: string): void {
  // Copy into a fresh buffer: pdf-lib may hand back a view over a larger
  // ArrayBuffer, and Blob would then include the surrounding bytes.
  const blob = new Blob([bytes.slice().buffer as ArrayBuffer], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  // Revoking immediately can cancel the download in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
